// src/components/OnboardingBanner.jsx
// App-wide setup progress banner — shown in AppLayout until isFullyOperational.
// Suppressed on /onboarding route (defense-in-depth; /onboarding is currently
// outside AppLayout in App.jsx routing).

import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ONBOARDING_STEP_ROUTES } from '../utils/onboarding';

const BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
const BANNER_DISMISSED_KEY = 'proirp-setup-banner-dismissed';
const BANNER_ROLES = new Set(['agent', 'agency_owner', 'agency_admin']);

const HARD_GATES = new Set(['baa', 'business_info', 'payment', 'phone_choice', 'twilio_setup', 'book_of_business']);

export default function OnboardingBanner() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(() =>
    sessionStorage.getItem(BANNER_DISMISSED_KEY) === 'true'
  );

  useEffect(() => {
    if (!user?.id || !BANNER_ROLES.has(user.role)) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    fetch(`${BASE}/onboarding/status`, { credentials: 'include' })
      .then(res => res.ok ? res.json() : Promise.reject())
      .then(json => { if (!cancelled) setStatus(json.status); })
      .catch(() => { /* silently fail — banner is non-critical */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user?.id, user?.role]);

  function dismiss() {
    sessionStorage.setItem(BANNER_DISMISSED_KEY, 'true');
    setDismissed(true);
  }

  // /onboarding suppression (defense-in-depth)
  if (location.pathname === '/onboarding') return null;

  // Role guard
  if (!user?.id || !BANNER_ROLES.has(user.role)) return null;

  // Loading guard (required)
  if (loading || !status) return null;

  // Fully operational — no banner needed
  if (status.isFullyOperational) return null;

  // Dismissed this session
  if (dismissed) return null;

  const { nextRequiredStep } = status;
  if (!nextRequiredStep) return null;

  let message;
  let ctaLabel;

  if (HARD_GATES.has(nextRequiredStep)) {
    message = 'Finish your account setup to unlock Pro IRP.';
    ctaLabel = 'Continue setup';
  } else if (nextRequiredStep === 'import_clients') {
    message = 'Add your first clients to unlock risk scoring and automations.';
    ctaLabel = 'Import clients';
  } else if (nextRequiredStep === 'review_automations') {
    message = 'Review your automation defaults to complete setup.';
    ctaLabel = 'Review automations';
  } else {
    return null;
  }

  const destination = ONBOARDING_STEP_ROUTES[nextRequiredStep];

  return (
    <div className="w-full bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center justify-between gap-3 text-sm">
      <span className="text-amber-800">{message}</span>
      <div className="flex items-center gap-3 flex-shrink-0">
        <button
          onClick={() => navigate(destination)}
          className="font-semibold text-amber-900 underline hover:no-underline whitespace-nowrap"
        >
          {ctaLabel}
        </button>
        <button
          onClick={dismiss}
          className="text-amber-600 hover:text-amber-800 text-base leading-none"
          aria-label="Dismiss"
        >
          ✕
        </button>
      </div>
    </div>
  );
}
